import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const SESSION_KEY = 'instamojoArchiveSession';

export function getInstamojoSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    if (!s.token || !s.expires_at) return null;
    if (Math.floor(Date.now() / 1000) >= s.expires_at) {
      localStorage.removeItem(SESSION_KEY);
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function clearInstamojoSession() {
  localStorage.removeItem(SESSION_KEY);
}

export default function InstamojoPasswordGate() {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuth();
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Skip gate if there's a still-valid session
  useEffect(() => {
    if (getInstamojoSession()) navigate('/instamojo/list', { replace: true });
  }, [navigate]);

  const isAuthorized =
    isSuperAdmin() || (user?.role === 'org_admin' && user?.can_approve_transfers);

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <Lock className="w-10 h-10 mx-auto mb-3 text-slate-400" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-600">
            Instamojo For Accounts is restricted to Super Admins and Directors.
          </p>
        </Card>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Please enter the password.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post('/instamojo-archive/verify-password', { password });
      const session = {
        token:      res.data.token,
        expires_at: res.data.expires_at,
        ttl_min:    res.data.ttl_min,
      };
      localStorage.setItem(SESSION_KEY, JSON.stringify(session));
      toast.success(`Access granted (valid for ${session.ttl_min} min).`);
      navigate('/instamojo/list', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Incorrect password.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-8">
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-violet-100">
          <Lock className="w-5 h-5 text-violet-600" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 text-center mb-1">
          Instamojo For Accounts
        </h1>
        <p className="text-sm text-slate-500 text-center mb-6">
          Enter the access password to continue.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="ima-pwd">Password</Label>
            <Input
              id="ima-pwd"
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={submitting}
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? 'Validating…' : 'Validate Password'}
          </Button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-6">
          Session lasts 1 hour. You may be asked again after that.
        </p>
      </Card>
    </div>
  );
}
